import * as THREE from 'three';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class for managing visual effects in the training track
 */
export class TrackEffectsManager {
    private scene: THREE.Scene;
    private scorePopups: Array<{
        mesh: THREE.Object3D, // Changed from THREE.Mesh to THREE.Object3D to accept both Mesh and Sprite
        startTime: number,
        update: (deltaTime: number) => boolean
    }> = [];
    private isInSlowMotion: boolean = false;
    private slowMotionStartTime: number = 0;
    private originalTimeScale: number = 1.0;
    private activeEffects: Map<string, THREE.Object3D> = new Map();
    private particleSystems: Array<{
        particles: THREE.Points,
        update: (deltaTime: number) => void
    }> = [];
    private dynamicLights: Array<{
        light: THREE.Light,
        update: (deltaTime: number) => void
    }> = [];
    private cameraEffects: {
        active: boolean,
        type: string,
        startTime: number,
        duration: number,
        intensity: number,
        update: (camera: THREE.Camera, deltaTime: number) => boolean
    } | null = null;
    
    /**
     * Constructor
     * @param scene The Three.js scene
     */
    constructor(scene: THREE.Scene) {
        this.scene = scene;
        
        // Inicjalizacja efektów dynamicznego oświetlenia
        this.initializeDynamicLighting();
    }
    
    /**
     * Inicjalizuje dynamiczne oświetlenie dla trasy treningowej
     */
    private initializeDynamicLighting(): void {
        // Dodaj światło punktowe, które będzie się poruszać wzdłuż trasy
        const trackLight = new THREE.PointLight(0x00ffff, 2, 20);
        trackLight.position.set(0, 5, 0);
        this.scene.add(trackLight);
        
        // Dodaj do listy dynamicznych świateł z funkcją aktualizacji
        this.dynamicLights.push({
            light: trackLight,
            update: (deltaTime: number) => {
                // Światło porusza się po okręgu
                const time = Date.now() * 0.001;
                trackLight.position.x = Math.sin(time * 0.5) * 15;
                trackLight.position.z = Math.cos(time * 0.5) * 15;
                
                // Zmiana koloru światła w czasie
                const hue = (time * 0.1) % 1;
                trackLight.color.setHSL(hue, 1, 0.5);
            }
        });
    }
    
    /**
     * Shows a score popup at the specified position
     * @param position The position to show the popup
     * @param text The text to display
     * @param color The color of the text
     */
    public showScorePopup(position: THREE.Vector3, text: string, color: number): void {
        // Create a canvas for the text
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 128;
        const context = canvas.getContext('2d');
        if (!context) return;
        
        // Draw text on canvas
        context.fillStyle = '#00000000'; // Transparent background
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.font = 'Bold 64px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = '#000000'; // Black outline
        context.fillText(text, 128 + 2, 64 + 2);
        context.fillStyle = `#${color.toString(16).padStart(6, '0')}`; // Convert hex color to CSS color
        context.fillText(text, 128, 64);
        
        // Create texture from canvas
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true
        });
        
        // Create sprite
        const sprite = new THREE.Sprite(material);
        sprite.position.copy(position);
        sprite.position.y += 2; // Position above the object
        sprite.scale.set(3, 1.5, 1);
        
        // Add to scene
        this.scene.add(sprite);
        
        // Store start time for animation
        const startTime = Date.now();
        
        // Add to score popups array with update function
        this.scorePopups.push({
            mesh: sprite,
            startTime,
            update: (deltaTime: number) => {
                const elapsed = Date.now() - startTime;
                const duration = TRAINING_TRACK_CONFIG.SCORE_DISPLAY_TIME;
                
                if (elapsed > duration) {
                    // Remove from scene when expired
                    this.scene.remove(sprite);
                    return false;
                }
                
                // Animate popup (float upward and fade out)
                const progress = elapsed / duration;
                sprite.position.y += deltaTime * 2;
                sprite.material.opacity = 1 - progress;
                
                return true;
            }
        });
    }
    
    /**
     * Creates an explosion effect at the specified position
     * @param position The position to create the explosion at
     * @param intensity Intensity of the explosion (1.0 = normal)
     */
    public createExplosion(position: THREE.Vector3, intensity: number = 1.0): void {
        // Create particle system for explosion
        const particleCount = Math.floor(100 * intensity);
        const particleGeometry = new THREE.BufferGeometry();
        const particleMaterial = new THREE.PointsMaterial({
            color: 0xff5500,
            size: 0.2,
            transparent: true,
            blending: THREE.AdditiveBlending
        });
        
        // Create particle positions
        const particlePositions = new Float32Array(particleCount * 3);
        const particleVelocities: THREE.Vector3[] = [];
        
        for (let i = 0; i < particleCount; i++) {
            // Random position within sphere
            const direction = new THREE.Vector3(
                Math.random() * 2 - 1,
                Math.random() * 2 - 1,
                Math.random() * 2 - 1
            ).normalize();
            
            const radius = Math.random() * 0.5;
            
            particlePositions[i * 3] = position.x + direction.x * radius;
            particlePositions[i * 3 + 1] = position.y + direction.y * radius;
            particlePositions[i * 3 + 2] = position.z + direction.z * radius;
            
            // Store velocity for animation
            particleVelocities.push(direction.multiplyScalar(2 + Math.random() * 3));
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        
        // Add to scene
        this.scene.add(particles);
        
        // Store start time for animation
        const startTime = Date.now();
        
        // Add to score popups array with update function (reusing the same array)
        this.scorePopups.push({
            mesh: particles as unknown as THREE.Mesh, // Type cast to fit the interface
            startTime,
            update: (deltaTime: number) => {
                const elapsed = Date.now() - startTime;
                const duration = 1000; // 1 second explosion
                
                if (elapsed > duration) {
                    // Remove from scene when expired
                    this.scene.remove(particles);
                    return false;
                }
                
                // Animate particles (expand outward and fade out)
                const progress = elapsed / duration;
                const positions = particleGeometry.attributes.position.array as Float32Array;
                
                for (let i = 0; i < particleCount; i++) {
                    // Update position based on velocity
                    positions[i * 3] += particleVelocities[i].x * deltaTime;
                    positions[i * 3 + 1] += particleVelocities[i].y * deltaTime;
                    positions[i * 3 + 2] += particleVelocities[i].z * deltaTime;
                }
                
                particleGeometry.attributes.position.needsUpdate = true;
                particleMaterial.opacity = 1 - progress;
                
                return true;
            }
        });
        
        // Add a flash of light
        const light = new THREE.PointLight(0xff5500, 5, 10);
        light.position.copy(position);
        this.scene.add(light);
        
        // Remove light after a short time
        setTimeout(() => {
            this.scene.remove(light);
        }, 200);
    }
    
    /**
     * Activates slow motion effect
     */
    public activateSlowMotion(): void {
        if (this.isInSlowMotion) return;
        
        this.isInSlowMotion = true;
        this.slowMotionStartTime = Date.now();
        
        // Store original time scale
        this.originalTimeScale = 1.0;
        
        // Apply slow motion effect
        const slowMotionFactor = TRAINING_TRACK_CONFIG.SLOW_MOTION_FACTOR;
        
        // Apply slow motion to game time scale (this would need to be implemented in the main game loop)
        // For now, we'll just dispatch an event that the game can listen to
        document.dispatchEvent(new CustomEvent('trainingTrack:slowMotion', { 
            detail: { 
                active: true,
                factor: slowMotionFactor
            } 
        }));
        
        console.log(`[TrackEffects] Activated slow motion: ${slowMotionFactor}x`);
    }
    
    /**
     * Updates slow motion effect
     * @param currentTime Current time in milliseconds
     */
    public updateSlowMotion(currentTime: number): void {
        if (!this.isInSlowMotion) return;
        
        const elapsed = currentTime - this.slowMotionStartTime;
        const duration = TRAINING_TRACK_CONFIG.SLOW_MOTION_DURATION;
        
        if (elapsed > duration) {
            // Deactivate slow motion
            this.isInSlowMotion = false;
            
            // Restore original time scale
            document.dispatchEvent(new CustomEvent('trainingTrack:slowMotion', { 
                detail: { 
                    active: false,
                    factor: 1.0
                } 
            }));
            
            console.log('[TrackEffects] Deactivated slow motion');
        }
    }
    
    /**
     * Updates the effects
     * @param deltaTime Time elapsed since last update in seconds
     * @param camera The camera to apply effects to
     */
    public update(deltaTime: number, camera?: THREE.Camera): void {
        // Update score popups and remove expired ones
        this.scorePopups = this.scorePopups.filter(popup => {
            return popup.update(deltaTime);
        });
        
        // Update slow motion effect if active
        if (this.isInSlowMotion) {
            this.updateSlowMotion(Date.now());
        }
        
        // Update particle systems
        this.particleSystems.forEach(system => system.update(deltaTime));
        
        // Update dynamic lights
        this.dynamicLights.forEach(light => light.update(deltaTime));
        
        // Update camera effects
        if (this.cameraEffects && this.cameraEffects.active && camera) {
            const effectActive = this.cameraEffects.update(camera, deltaTime);
            if (!effectActive) {
                this.cameraEffects.active = false;
            }
        }
    }
    
    /**
     * Creates a fire effect at the specified position
     * @param position The position to create the fire at
     * @param scale The scale of the fire effect
     * @param color The color of the fire
     */
    public createFireEffect(position: THREE.Vector3, scale: number = 1.0, color: number = 0xff5500): void {
        // Create particle system for fire
        const particleCount = 100 * scale;
        const particleGeometry = new THREE.BufferGeometry();
        const particleMaterial = new THREE.PointsMaterial({
            color: color,
            size: 0.2 * scale,
            transparent: true,
            blending: THREE.AdditiveBlending
        });
        
        // Create particle positions
        const particlePositions = new Float32Array(particleCount * 3);
        for (let i = 0; i < particleCount; i++) {
            const radius = Math.random() * scale;
            const theta = Math.random() * Math.PI * 2;
            
            particlePositions[i * 3] = Math.cos(theta) * radius * 0.5;
            particlePositions[i * 3 + 1] = Math.random() * scale * 2;
            particlePositions[i * 3 + 2] = Math.sin(theta) * radius * 0.5;
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
        const particles = new THREE.Points(particleGeometry, particleMaterial);
        particles.position.copy(position);
        
        // Add to scene
        this.scene.add(particles);
        
        // Add to particle systems with update function
        this.particleSystems.push({
            particles,
            update: (deltaTime: number) => {
                const positions = particles.geometry.attributes.position.array as Float32Array;
                
                for (let i = 0; i < particleCount; i++) {
                    // Make particles move upward
                    positions[i * 3 + 1] += (0.5 + Math.random() * 0.5) * deltaTime;
                    
                    // Add some horizontal movement
                    positions[i * 3] += (Math.random() - 0.5) * 0.1 * deltaTime;
                    positions[i * 3 + 2] += (Math.random() - 0.5) * 0.1 * deltaTime;
                    
                    // Reset particles that go too high
                    if (positions[i * 3 + 1] > scale * 2) {
                        positions[i * 3 + 1] = 0;
                        
                        const radius = Math.random() * scale * 0.5;
                        const theta = Math.random() * Math.PI * 2;
                        positions[i * 3] = Math.cos(theta) * radius;
                        positions[i * 3 + 2] = Math.sin(theta) * radius;
                    }
                }
                
                particles.geometry.attributes.position.needsUpdate = true;
                
                // Rotate the particle system slightly for more dynamic effect
                particles.rotation.y += deltaTime * 0.2;
                
                return true;
            }
        });
        
        // Add a point light for the fire
        const fireLight = new THREE.PointLight(color, 1, scale * 10);
        fireLight.position.copy(position);
        fireLight.position.y += scale * 0.5;
        this.scene.add(fireLight);
        
        // Add to dynamic lights
        this.dynamicLights.push({
            light: fireLight,
            update: (deltaTime: number) => {
                // Make the light flicker
                fireLight.intensity = 1 + Math.random() * 0.5;
            }
        });
    }
    
    /**
     * Applies a camera effect
     * @param type The type of camera effect
     * @param duration The duration of the effect in milliseconds
     * @param intensity The intensity of the effect (0.0 to 1.0)
     */
    public applyCameraEffect(type: string, duration: number = 2000, intensity: number = 1.0): void {
        this.cameraEffects = {
            active: true,
            type,
            startTime: Date.now(),
            duration,
            intensity,
            update: (camera: THREE.Camera, deltaTime: number) => {
                const elapsed = Date.now() - this.cameraEffects!.startTime;
                const progress = Math.min(1.0, elapsed / this.cameraEffects!.duration);
                const remainingProgress = 1.0 - progress;
                
                switch (type) {
                    case 'shake':
                        // Camera shake effect
                        const shakeAmount = remainingProgress * intensity * 0.2;
                        camera.position.x += (Math.random() - 0.5) * shakeAmount;
                        camera.position.y += (Math.random() - 0.5) * shakeAmount;
                        camera.position.z += (Math.random() - 0.5) * shakeAmount;
                        break;
                        
                    case 'zoom':
                        // Camera zoom effect for perspective camera
                        if (camera instanceof THREE.PerspectiveCamera) {
                            const zoomFactor = 1.0 + remainingProgress * intensity * 0.2;
                            camera.fov = camera.fov / zoomFactor;
                            camera.updateProjectionMatrix();
                        }
                        break;
                        
                    case 'tilt':
                        // Camera tilt effect
                        const tiltAmount = Math.sin(elapsed * 0.01) * remainingProgress * intensity * 0.1;
                        camera.rotation.z = tiltAmount;
                        break;
                }
                
                return elapsed < duration;
            }
        };
    }
    
    /**
     * Creates a trail effect behind an object
     * @param position The position of the object
     * @param color The color of the trail
     * @param length The length of the trail
     */
    public createTrailEffect(position: THREE.Vector3, color: number = 0x00ffff, length: number = 10): string {
        // Create a line for the trail
        const trailGeometry = new THREE.BufferGeometry();
        const trailMaterial = new THREE.LineBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.7,
            blending: THREE.AdditiveBlending
        });
        
        // Create points for the trail
        const points = [];
        for (let i = 0; i < length; i++) {
            points.push(position.clone());
        }
        
        trailGeometry.setFromPoints(points);
        const trail = new THREE.Line(trailGeometry, trailMaterial);
        this.scene.add(trail);
        
        // Generate a unique ID for this trail
        const trailId = `trail_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
        this.activeEffects.set(trailId, trail);
        
        return trailId;
    }
    
    /**
     * Updates a trail effect
     * @param trailId The ID of the trail to update
     * @param newPosition The new position for the trail
     */
    public updateTrailEffect(trailId: string, newPosition: THREE.Vector3): void {
        const trail = this.activeEffects.get(trailId) as THREE.Line;
        if (!trail) return;
        
        // Get the points from the geometry
        const positions = trail.geometry.attributes.position.array as Float32Array;
        
        // Shift all points back
        for (let i = positions.length / 3 - 1; i > 0; i--) {
            positions[i * 3] = positions[(i - 1) * 3];
            positions[i * 3 + 1] = positions[(i - 1) * 3 + 1];
            positions[i * 3 + 2] = positions[(i - 1) * 3 + 2];
        }
        
        // Set the first point to the new position
        positions[0] = newPosition.x;
        positions[1] = newPosition.y;
        positions[2] = newPosition.z;
        
        trail.geometry.attributes.position.needsUpdate = true;
    }
    
    /**
     * Checks if slow motion is currently active
     * @returns True if slow motion is active, false otherwise
     */
    public isSlowMotionActive(): boolean {
        return this.isInSlowMotion;
    }
    
    /**
     * Clears all effects
     */
    public clear(): void {
        // Remove all score popups from the scene
        this.scorePopups.forEach(popup => {
            this.scene.remove(popup.mesh);
        });
        
        // Clear collections
        this.scorePopups = [];
        
        // Reset state
        this.isInSlowMotion = false;
        this.slowMotionStartTime = 0;
    }
}